import React, { createContext, useContext, useState, useEffect } from 'react';

const AdminAuthContext = createContext(null);

export const useAdminAuth = () => {
  const context = useContext(AdminAuthContext);
  if (!context) {
    throw new Error('useAdminAuth must be used within an AdminAuthProvider');
  }
  return context;
};

export const AdminAuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('admin_token'));
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState([]);

  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    const validateToken = async () => {
      if (!token) {
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${backendUrl}/api/admin/me`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setAdmin(data);
          setPermissions(data.permissions || []);
        } else {
          // Token invalid
          localStorage.removeItem('admin_token');
          setToken(null);
          setAdmin(null);
        }
      } catch (error) {
        console.error('Admin token validation error:', error);
      } finally {
        setLoading(false);
      }
    };

    validateToken();
  }, [token, backendUrl]);

  const login = async (email, password) => {
    const response = await fetch(`${backendUrl}/api/admin/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ email, password })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Login fehlgeschlagen');
    }

    const data = await response.json();
    localStorage.setItem('admin_token', data.access_token);
    setToken(data.access_token);
    setAdmin({
      id: data.admin_id,
      email: data.email,
      name: data.name,
      role: data.role
    });
    setPermissions(data.permissions || []);
    return data;
  };

  const logout = () => {
    localStorage.removeItem('admin_token');
    setToken(null);
    setAdmin(null);
    setPermissions([]);
  };

  const hasPermission = (permission) => {
    if (permissions.includes('*')) return true;
    return permissions.includes(permission);
  };

  const value = {
    admin,
    token,
    loading,
    permissions,
    login,
    logout,
    hasPermission,
    isAuthenticated: !!admin
  };

  return (
    <AdminAuthContext.Provider value={value}>
      {children}
    </AdminAuthContext.Provider>
  );
};
